import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useRef, useState, Children, useEffect, cloneElement } from "react";
import stitches from "../theme.config.es.js";
import { domProps } from "../../utils/domProps.es.js";
import EzPopover from "../EzPopover/EzPopover.es.js";
import { useId } from "@radix-ui/react-id";
const tooltip = stitches.css({
  position: "relative",
  padding: "$tooltip-padding",
  maxWidth: "300px",
  boxShadow: "$sm",
  whiteSpace: "pre-line",
  color: "$tooltip-text",
  border: "1px solid $tooltip-border",
  backgroundColor: "$tooltip-border"
});
const popper = stitches.css({
  zIndex: "$tooltip-z"
  // this is hard-coded in theme.config.ts for now, but we should really pull it in from mui when we convert tooltips to mui
});
const arrow = stitches.css({
  position: "absolute",
  pointerEvents: "none",
  fill: "$tooltip-bg"
});
const arrowPosition = stitches.css({
  [`[data-popper-placement^='top'] > &`]: {
    bottom: 5,
    svg: { transform: "rotate(180deg) translate(50%, 0)" }
  },
  [`[data-popper-placement^='right'] > &`]: {
    left: 5,
    svg: { transform: "rotate(-90deg) translate(50%, 0)" }
  },
  [`[data-popper-placement^='bottom'] > &`]: {
    top: -5,
    svg: { transform: "translate(-50%, 0)" }
  },
  [`[data-popper-placement^='left'] > &`]: {
    right: 5,
    svg: { transform: "rotate(90deg) translate(-50%, 0)" }
  }
});
const reset = stitches.css({ my: 0 });
const EzTooltip = ({
  children,
  message,
  position = "vertical",
  onShowTooltip,
  ...rest
}) => {
  const id = useId();
  const targetRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const onMouseEnterEvent = () => setIsHovered(true);
  const onMouseLeaveEvent = () => setIsHovered(false);
  const onFocusEvent = () => setIsFocused(true);
  const onBlurEvent = () => setIsFocused(false);
  const showTooltip = isHovered || isFocused;
  const child = Children.only(children);
  const onShowTooltipRef = useRef(onShowTooltip);
  onShowTooltipRef.current = onShowTooltip;
  useEffect(() => {
    var _a;
    if (showTooltip) (_a = onShowTooltipRef.current) == null ? void 0 : _a.call(onShowTooltipRef);
  }, [showTooltip, onShowTooltipRef]);
  const childProps = {
    "aria-describedby": id,
    onMouseEnter: onMouseEnterEvent,
    onMouseLeave: onMouseLeaveEvent,
    onFocus: onFocusEvent,
    onBlur: onBlurEvent,
    ref: targetRef
  };
  const props = domProps(rest, tooltip());
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    cloneElement(child, childProps),
    showTooltip && /* @__PURE__ */ jsxs(
      EzPopover,
      {
        targetRef,
        placement: position === "horizontal" ? "right" : "bottom",
        showArrow: true,
        className: popper(),
        children: [
          /* @__PURE__ */ jsx("div", { "data-popper-arrow": true, className: arrowPosition(), children: /* @__PURE__ */ jsxs("svg", { width: "10", height: "10", className: arrow(), children: [
            /* @__PURE__ */ jsx("path", { d: "M0 5l5-5 5 5z" }),
            /* @__PURE__ */ jsx("path", { d: "M1.5 5L5 1.5 8.5 5z" })
          ] }) }),
          /* @__PURE__ */ jsx("div", { role: "tooltip", id, tabIndex: -1, ...props, children: message && /* @__PURE__ */ jsx("p", { className: reset(), children: message }) })
        ]
      }
    )
  ] });
};
export {
  EzTooltip as default
};
//# sourceMappingURL=EzTooltip.es.js.map
